import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { subscriptionApi } from '@/services/api'
import { flutterBridge } from '@/bridge/flutter-bridge'
import Button from '@/components/ui/Button'
import Icon from '@/components/ui/Icon'
import styles from './Subscription.module.css'

function SuccessModal({
  trialEndsAt,
  onContinue,
}: {
  trialEndsAt?: string
  onContinue: () => void
}) {
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '7 days from now'
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  return (
    <div className={styles.successOverlay}>
      <div className={styles.successModal}>
        <div className={styles.successIcon}><Icon name="check" size={40} /></div>
        <h2 className={styles.successTitle}>Welcome to Premium!</h2>
        <p className={styles.successSubtitle}>You have full access to all activities</p>

        <div className={styles.successDivider} />

        <div className={styles.successDetails}>
          {trialEndsAt && (
            <div className={styles.successDetailRow}>
              <span className={styles.successDetailLabel}>Free trial ends</span>
              <span className={styles.successDetailValue}>{formatDate(trialEndsAt)}</span>
            </div>
          )}
        </div>

        <Button variant="primary" fullWidth size="lg" onClick={onContinue}>
          Start Exploring
        </Button>
      </div>
    </div>
  )
}

function FailureModal({
  message,
  onRetry,
  onDismiss,
}: {
  message: string
  onRetry: () => void
  onDismiss: () => void
}) {
  return (
    <div className={styles.failureOverlay}>
      <div className={styles.failureModal}>
        <div className={styles.failureIcon}>!</div>
        <h2 className={styles.failureTitle}>Something went wrong</h2>
        <p className={styles.failureSubtitle}>{message}</p>

        <div className={styles.failureActions}>
          <Button variant="primary" fullWidth size="lg" onClick={onRetry}>
            Try Again
          </Button>
          <button className={styles.failureSecondary} onClick={onDismiss}>
            Go Back
          </button>
        </div>
      </div>
    </div>
  )
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function Subscription() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const setUser = useAuthStore((state) => state.setUser)
  const [loading, setLoading] = useState(true)
  const [starting, setStarting] = useState(false)
  const [restoring, setRestoring] = useState(false)
  const [productPrice, setProductPrice] = useState<string | null>(null)
  const [showSuccess, setShowSuccess] = useState<{ trialEndsAt?: string } | null>(null)
  const [showFailure, setShowFailure] = useState<{ message: string } | null>(null)

  useEffect(() => {
    // Load subscription status
    subscriptionApi.getStatus()
      .then((res) => {
        if (user && res.data) {
          setUser({
            ...user,
            subscription: {
              status: res.data.status,
              trial_ends_at: res.data.trial_ends_at,
              current_period_end: res.data.current_period_end,
              trial_days_remaining: res.data.trial_days_remaining ?? 0,
              is_premium: res.data.is_premium,
            },
          })
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))

    // Load product prices from store
    if (flutterBridge.isInFlutter) {
      flutterBridge.getSubscriptionProducts()
        .then((result) => {
          if (result.products.length > 0) {
            setProductPrice(result.products[0].price)
          }
        })
        .catch(() => {})
    }
  }, [])

  // Listen for IAP events from Flutter
  useEffect(() => {
    if (!flutterBridge.isInFlutter) return

    const unsubPurchased = flutterBridge.onEvent('iap:purchased', async (data) => {
      try {
        const result = await subscriptionApi.verifyStoreReceipt({
          platform: data.platform as 'apple' | 'google',
          receipt_token: data.receiptToken as string,
          product_id: data.productId as string,
        })

        if (user && result.data.subscription) {
          setUser({
            ...user,
            subscription: {
              status: result.data.subscription.status,
              trial_ends_at: result.data.subscription.trial_ends_at,
              trial_days_remaining: result.data.subscription.trial_days_remaining,
              is_premium: result.data.subscription.is_premium,
            },
          })
        }

        setStarting(false)
        setShowSuccess({ trialEndsAt: result.data.subscription?.trial_ends_at })
      } catch {
        setStarting(false)
        setShowFailure({ message: 'Purchase verification failed. Please try again.' })
      }
    })

    const unsubRestored = flutterBridge.onEvent('iap:restored', async (data) => {
      try {
        await subscriptionApi.verifyStoreReceipt({
          platform: data.platform as 'apple' | 'google',
          receipt_token: data.receiptToken as string,
          product_id: data.productId as string,
        })

        // Refresh status
        const res = await subscriptionApi.getStatus()
        if (user && res.data) {
          setUser({
            ...user,
            subscription: {
              status: res.data.status,
              trial_ends_at: res.data.trial_ends_at,
              current_period_end: res.data.current_period_end,
              trial_days_remaining: res.data.trial_days_remaining ?? 0,
              is_premium: res.data.is_premium,
            },
          })
        }
        setRestoring(false)
      } catch {
        setRestoring(false)
        setShowFailure({ message: 'Could not restore purchases. Please try again.' })
      }
    })

    const unsubFailed = flutterBridge.onEvent('iap:failed', (data) => {
      setStarting(false)
      setRestoring(false)
      const code = data.code as string
      if (code === 'CANCELLED') return // User cancelled, no error to show
      setShowFailure({ message: (data.error as string) || 'Purchase failed' })
    })

    return () => {
      unsubPurchased()
      unsubRestored()
      unsubFailed()
    }
  }, [user])

  const subscription = user?.subscription
  const hasActiveAccess = subscription?.status === 'trial' || subscription?.status === 'active'

  const handleStartTrial = async () => {
    setStarting(true)
    setShowFailure(null)

    try {
      await flutterBridge.purchaseSubscription('mellomap_premium_monthly')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start purchase'
      setShowFailure({ message })
      setStarting(false)
    }
  }

  const handleRestore = async () => {
    setRestoring(true)
    try {
      await flutterBridge.restorePurchases()
    } catch {
      setRestoring(false)
      setShowFailure({ message: 'Could not restore purchases.' })
    }
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading...</div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>← Back</button>
      </header>

      {hasActiveAccess ? (
        <section className={styles.statusSection}>
          <div className={styles.statusBadge}>
            {subscription?.status === 'trial' ? 'Free Trial' : 'Premium'}
          </div>
          <h1 className={styles.title}>
            {subscription?.status === 'trial'
              ? `${subscription.trial_days_remaining} days remaining`
              : 'Active Subscription'}
          </h1>
          <p className={styles.subtitle}>
            {subscription?.status === 'trial'
              ? 'Enjoy full access to all activities'
              : 'Thank you for being a subscriber'}
          </p>

          <div className={styles.paymentInfo}>
            {subscription?.status === 'trial' && subscription.trial_ends_at && (
              <>
                <div className={styles.paymentInfoLabel}>Trial ends</div>
                <div className={styles.paymentInfoValue}>
                  {formatDate(subscription.trial_ends_at)}
                </div>
              </>
            )}
            {subscription?.status === 'active' && subscription.current_period_end && (
              <div className={styles.paymentInfoRow}>
                <span className={styles.paymentInfoLabel}>Renews on</span>
                <span className={styles.paymentInfoValue}>{formatDate(subscription.current_period_end)}</span>
              </div>
            )}
          </div>

          <p className={styles.manageNote}>
            Manage your subscription in your device's Settings
          </p>
        </section>
      ) : (
        <section className={styles.heroSection}>
          <span className={styles.heroIcon}><Icon name="sparkles" size={48} /></span>
          <h1 className={styles.title}>Unlock Premium</h1>
          <p className={styles.subtitle}>Get full access to 100+ expert-designed activities</p>
        </section>
      )}

      <section className={styles.benefits}>
        <h2 className={styles.benefitsTitle}>What you get</h2>
        <ul className={styles.benefitsList}>
          <li className={styles.benefitItem}>
            <span className={styles.benefitIcon}><Icon name="target" size={24} /></span>
            <div>
              <h3 className={styles.benefitName}>All Activities</h3>
              <p className={styles.benefitDesc}>Access to 100+ calming activities</p>
            </div>
          </li>
          <li className={styles.benefitItem}>
            <span className={styles.benefitIcon}><Icon name="bar-chart-3" size={24} /></span>
            <div>
              <h3 className={styles.benefitName}>Personalized Insights</h3>
              <p className={styles.benefitDesc}>Track what works for your child</p>
            </div>
          </li>
          <li className={styles.benefitItem}>
            <span className={styles.benefitIcon}><Icon name="message-circle" size={24} /></span>
            <div>
              <h3 className={styles.benefitName}>Expert Support</h3>
              <p className={styles.benefitDesc}>WhatsApp access to OT experts</p>
            </div>
          </li>
        </ul>
      </section>

      <footer className={styles.footer}>
        {subscription?.status !== 'active' && (
          <>
            <div className={styles.pricing}>
              <p className={styles.price}>{productPrice || '₹1999'}/month</p>
              <p className={styles.pricingNote}>
                Payment is charged to your Apple ID / Google Play account.
                Auto-renews monthly unless cancelled at least 24 hours before
                the period ends. Manage or cancel anytime in your device
                Settings.
              </p>
            </div>
            <Button variant="primary" fullWidth size="lg" onClick={handleStartTrial} disabled={starting}>
              {starting
                ? 'Processing…'
                : subscription?.status === 'trial'
                  ? `Upgrade now — ${productPrice || '₹1999'}/month`
                  : `Subscribe — ${productPrice || '₹1999'}/month`}
            </Button>
            <button className={styles.restoreLink} onClick={handleRestore} disabled={restoring}>
              {restoring ? 'Restoring...' : 'Restore Purchases'}
            </button>
            <p className={styles.legalLinks}>
              <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Use</a>
              {' · '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            </p>
          </>
        )}
      </footer>

      {showSuccess && (
        <SuccessModal
          trialEndsAt={showSuccess.trialEndsAt}
          onContinue={() => {
            setShowSuccess(null)
            navigate('/')
          }}
        />
      )}

      {showFailure && (
        <FailureModal
          message={showFailure.message}
          onRetry={() => {
            setShowFailure(null)
            handleStartTrial()
          }}
          onDismiss={() => {
            setShowFailure(null)
          }}
        />
      )}
    </div>
  )
}
